import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type ActivityItem, type ClubStats } from "../api";
import { Avatar } from "../components/Avatar";
import { BookCover } from "../components/BookCover";
import { Card, SectionHeading, Spinner } from "../components/ui";
import { StarRating } from "../components/StarRating";

export default function InsightsPage() {
  const stats = useQuery({
    queryKey: ["stats"],
    queryFn: () => api.get<ClubStats>("/stats"),
  });

  const activity = useQuery({
    queryKey: ["activity"],
    queryFn: () => api.get<{ activity: ActivityItem[] }>("/activity?limit=20"),
  });

  if (stats.isLoading) return <Spinner />;
  if (!stats.data) return null;

  const { totals, topRated, topPickers, byYear } = stats.data;

  return (
    <div className="flex flex-col gap-12">
      <SectionHeading
        eyebrow="Where we are"
        title="Club insights"
        description="A small portrait of our reading life."
      />

      {/* Headline numbers */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BigStat label="Books in library" value={totals.books} />
        <BigStat label="Pages on the shelf" value={totals.pages.toLocaleString()} />
        <BigStat label="Meetings held" value={totals.meetings} />
        <BigStat
          label="Average rating"
          value={totals.averageRating ? totals.averageRating.toFixed(1) : "—"}
          sub={totals.ratingCount ? `${totals.ratingCount} ratings` : undefined}
        />
        <BigStat label="Members" value={totals.members} />
        <BigStat label="Notes written" value={totals.notes} />
        <BigStat label="Quotes saved" value={totals.bookmarks} />
        <BigStat label="Books finished" value={totals.finished} />
      </div>

      {/* Top rated */}
      {topRated.length > 0 ? (
        <section className="flex flex-col gap-6">
          <h2 className="font-serif text-2xl text-plum tracking-tight">Best loved</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {topRated.map((b) => (
              <Link key={b.id} to={`/book/${b.id}`} className="flex flex-col items-center text-center group">
                <BookCover url={b.coverUrl} title={b.title} author={b.author} size="md" className="group-hover:-translate-y-1" />
                <h3 className="font-serif text-sm text-plum leading-tight line-clamp-2 mt-3">
                  {b.title}
                </h3>
                <p className="text-plum-mute text-xs italic mt-0.5">{b.author}</p>
                <div className="mt-2">
                  <StarRating value={b.averageStars} readOnly size={13} showCount={b.ratingCount} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      {/* Top pickers */}
      {topPickers.length > 0 ? (
        <Card accent>
          <h2 className="font-serif text-2xl text-plum tracking-tight mb-6">Who picks well</h2>
          <ul className="flex flex-col divide-y divide-cream-200">
            {topPickers.map((p) => (
              <li key={p.userId} className="flex items-center gap-4 py-3">
                <Link to={`/members/${p.userId}`} className="flex items-center gap-3 flex-1 hover:text-rose">
                  <Avatar name={p.name} email={p.email} size="sm" />
                  <span className="text-plum">{p.name ?? p.email.split("@")[0]}</span>
                </Link>
                <span className="text-sm text-plum-soft">{p.picks} {p.picks === 1 ? "pick" : "picks"}</span>
                {p.avgPickedRating ? (
                  <div className="w-32 flex justify-end">
                    <StarRating value={p.avgPickedRating} readOnly size={13} />
                  </div>
                ) : (
                  <span className="w-32 text-right text-xs text-plum-mute italic">no ratings yet</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/* By year */}
      {byYear.length > 0 ? (
        <Card>
          <h2 className="font-serif text-2xl text-plum tracking-tight mb-5">Year by year</h2>
          <div className="flex flex-col gap-3">
            {byYear.map((y) => {
              const max = Math.max(...byYear.map((x) => x.count));
              const pct = (y.count / max) * 100;
              return (
                <div key={y.year} className="flex items-center gap-4">
                  <div className="w-16 font-mono text-sm text-plum-soft">{y.year}</div>
                  <div className="flex-1 h-3 rounded-full bg-cream-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blush to-rose rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-32 text-right text-xs text-plum-mute">
                    {y.count} {y.count === 1 ? "book" : "books"} · {y.pages.toLocaleString()} pages
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      ) : null}

      {/* Activity */}
      <section className="flex flex-col gap-5">
        <h2 className="font-serif text-2xl text-plum tracking-tight">Recent activity</h2>
        {activity.isLoading ? (
          <Spinner />
        ) : (activity.data?.activity.length ?? 0) === 0 ? (
          <p className="text-plum-mute italic">Nothing yet.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {activity.data!.activity.map((a) => (
              <li key={a.id} className="flex items-start gap-3 py-2.5 border-b border-cream-100">
                {a.user ? (
                  <Avatar name={a.user.name} email={a.user.email} size="sm" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-cream-200" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-plum leading-snug">
                    <span className="font-medium">{a.user?.name ?? a.user?.email.split("@")[0] ?? "Someone"}</span>{" "}
                    {actionFor(a)}
                    {a.book ? (
                      <Link to={`/book/${a.book.id}`} className="text-rose hover:text-rose-dark italic">
                        {" "}{a.book.title}
                      </Link>
                    ) : null}
                  </p>
                  <p className="text-xs text-plum-mute mt-0.5">{relativeTime(a.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function BigStat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Card className="text-center !p-5">
      <div className="font-serif text-3xl text-plum leading-none">{value}</div>
      <div className="text-[0.65rem] uppercase tracking-[0.16em] text-plum-mute mt-2">{label}</div>
      {sub ? <div className="text-xs text-plum-mute mt-1">{sub}</div> : null}
    </Card>
  );
}

function actionFor(a: ActivityItem): string {
  switch (a.type) {
    case "book_added": return "added";
    case "book_finished": return "finished";
    case "book_set_current": return "made current";
    case "note_added": return "left a note on";
    case "rsvp_set": return "RSVP'd to";
    case "rating_added": return `rated${a.payload?.stars ? ` ${a.payload.stars}★` : ""}`;
    case "progress_updated": return "updated their progress on";
    case "bookmark_saved": return "saved a quote from";
    case "meeting_scheduled": return "scheduled a meeting";
    case "turn_advanced": return "advanced the turn rotation";
    default: return "did something with";
  }
}

function relativeTime(s: string): string {
  const t = new Date(s.replace(" ", "T") + "Z").getTime();
  const delta = Date.now() - t;
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (delta < minute) return "just now";
  if (delta < hour) return `${Math.floor(delta / minute)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  if (delta < 7 * day) return `${Math.floor(delta / day)}d ago`;
  return new Date(t).toLocaleDateString();
}
